import React from "react";
import { motion } from "framer-motion";
import { 
  ExternalLink, 
  Heart, 
  MessageCircle, 
  Repeat2,
  Verified,
  TrendingUp,
  TrendingDown,
  Minus,
} from "lucide-react";
import xLogo from "@/assets/x_logo_16.png";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface TweetMentionCardProps {
  /** Tweet URL or ID */
  tweetUrl?: string;
  tweetId?: string;
  /** Pre-loaded tweet data (optional) */
  tweetData?: TweetData;
  /** Display variant */
  variant?: "inline" | "card" | "compact";
}

interface TweetData {
  id: string;
  url?: string;
  content: string;
  author: {
    name: string;
    username: string;
    avatar?: string;
    verified?: boolean;
  };
  createdAt: string;
  metrics?: {
    likes?: number;
    retweets?: number;
    replies?: number;
  };
  sentiment?: "bullish" | "bearish" | "neutral";
  entities?: Array<{ ticker: string; stance?: string }>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function formatTimeAgo(dateStr: string): string {
  const delta = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(delta / 60000);
  if (minutes < 1) return "now";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return new Date(dateStr).toLocaleDateString();
}

function formatCount(num?: number): string {
  if (num === undefined || num === null) return "0";
  if (num >= 1e6) return `${(num / 1e6).toFixed(1)}M`;
  if (num >= 1e3) return `${(num / 1e3).toFixed(1)}K`;
  return num.toString();
}

// ─────────────────────────────────────────────────────────────────────────────
// Sentiment Badge
// ─────────────────────────────────────────────────────────────────────────────

const SentimentBadge: React.FC<{ sentiment: "bullish" | "bearish" | "neutral" | undefined }> = ({ sentiment }) => {
  if (!sentiment) return null;

  const config = {
    bullish: { 
      icon: <TrendingUp className="h-3 w-3" />, 
      color: "text-emerald-400", 
      bg: "bg-emerald-400/10",
      border: "border-emerald-400/30",
    },
    bearish: { 
      icon: <TrendingDown className="h-3 w-3" />, 
      color: "text-rose-400", 
      bg: "bg-rose-400/10",
      border: "border-rose-400/30",
    },
    neutral: { 
      icon: <Minus className="h-3 w-3" />, 
      color: "text-gray-400", 
      bg: "bg-gray-400/10",
      border: "border-gray-400/30",
    },
  }[sentiment];

  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-medium uppercase tracking-wide ${config.color} ${config.bg} ${config.border}`}>
      {config.icon}
      {sentiment}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Inline Variant
// ─────────────────────────────────────────────────────────────────────────────

const InlineVariant: React.FC<{ tweet: TweetData }> = ({ tweet }) => (
  <a
    href={tweet.url || `https://x.com/i/status/${tweet.id}`}
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-gray-800/60 hover:bg-gray-700/60 border border-gray-700/50 hover:border-sky-500/30 transition-all group"
  >
    <img src={xLogo} alt="X" className="h-3.5 w-3.5 opacity-70" />
    <div className="flex items-center gap-1.5">
      {tweet.author.avatar ? (
        <img 
          src={tweet.author.avatar} 
          alt={tweet.author.username}
          className="h-4 w-4 rounded-full"
        />
      ) : (
        <div className="h-4 w-4 rounded-full bg-sky-500/20 flex items-center justify-center">
          <span className="text-[8px] font-bold text-sky-400">
            {tweet.author.name[0]}
          </span>
        </div>
      )}
      <span className="text-sm text-white font-medium">@{tweet.author.username}</span>
    </div>
    <span className="text-xs text-gray-400 max-w-[200px] truncate">
      {tweet.content.slice(0, 60)}...
    </span>
    <ExternalLink className="h-3 w-3 text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity" />
  </a>
);

// ─────────────────────────────────────────────────────────────────────────────
// Card Variant
// ─────────────────────────────────────────────────────────────────────────────

const CardVariant: React.FC<{ tweet: TweetData }> = ({ tweet }) => (
  <motion.div
    initial={{ opacity: 0, y: 10, scale: 0.95 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    className="relative rounded-xl border border-gray-700/50 bg-gradient-to-br from-gray-900/90 to-gray-800/90 backdrop-blur-xl overflow-hidden w-80"
  >
    {/* X brand accent */}
    <div className="absolute top-0 right-0 w-20 h-20 opacity-5">
      <img src={xLogo} alt="" className="w-full h-full object-cover" />
    </div>

    <div className="relative z-10 p-4">
      {/* Header */}
      <div className="flex items-start gap-3 mb-3">
        {tweet.author.avatar ? (
          <img 
            src={tweet.author.avatar} 
            alt={tweet.author.username}
            className="h-10 w-10 rounded-full ring-2 ring-gray-700/50"
          />
        ) : (
          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center ring-2 ring-gray-700/50">
            <span className="text-sm font-bold text-white">
              {tweet.author.name[0]}
            </span>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="font-semibold text-white truncate">{tweet.author.name}</span>
            {tweet.author.verified && (
              <Verified className="h-4 w-4 text-sky-400 flex-shrink-0" />
            )}
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>@{tweet.author.username}</span>
            <span>·</span>
            <span>{formatTimeAgo(tweet.createdAt)}</span>
          </div>
        </div>
        <a
          href={tweet.url || `https://x.com/i/status/${tweet.id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="p-1.5 rounded-lg hover:bg-gray-700/50 transition-colors"
        >
          <img src={xLogo} alt="X" className="h-4 w-4 opacity-60 hover:opacity-100" />
        </a>
      </div>

      {/* Content */}
      <p className="text-sm text-gray-200 leading-relaxed mb-3 line-clamp-4">
        {tweet.content}
      </p>

      {/* Sentiment & Entities */}
      {(tweet.sentiment || (tweet.entities && tweet.entities.length > 0)) && (
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <SentimentBadge sentiment={tweet.sentiment} />
          {tweet.entities?.map((e) => (
            <span 
              key={e.ticker}
              className="text-xs px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-300 border border-violet-500/30"
            >
              ${e.ticker}
            </span>
          ))}
        </div>
      )}

      {/* Metrics */}
      {tweet.metrics && (
        <div className="flex items-center gap-4 text-xs text-gray-500 pt-3 border-t border-gray-700/50">
          <div className="flex items-center gap-1.5 hover:text-rose-400 transition-colors cursor-default">
            <Heart className="h-3.5 w-3.5" />
            <span>{formatCount(tweet.metrics.likes)}</span>
          </div>
          <div className="flex items-center gap-1.5 hover:text-emerald-400 transition-colors cursor-default">
            <Repeat2 className="h-3.5 w-3.5" />
            <span>{formatCount(tweet.metrics.retweets)}</span>
          </div>
          <div className="flex items-center gap-1.5 hover:text-sky-400 transition-colors cursor-default">
            <MessageCircle className="h-3.5 w-3.5" />
            <span>{formatCount(tweet.metrics.replies)}</span>
          </div>
          <a
            href={tweet.url || `https://x.com/i/status/${tweet.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto flex items-center gap-1 text-sky-400 hover:text-sky-300 transition-colors"
          >
            <span>View</span>
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      )}
    </div>
  </motion.div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Compact Variant
// ─────────────────────────────────────────────────────────────────────────────

const CompactVariant: React.FC<{ tweet: TweetData }> = ({ tweet }) => (
  <a
    href={tweet.url || `https://x.com/i/status/${tweet.id}`}
    target="_blank"
    rel="noopener noreferrer"
    className="block rounded-lg border border-gray-700/40 bg-gray-800/40 p-3 hover:bg-gray-800/60 hover:border-sky-500/30 transition-all"
  >
    <div className="flex items-start gap-2.5">
      {tweet.author.avatar ? (
        <img 
          src={tweet.author.avatar} 
          alt={tweet.author.username}
          className="h-8 w-8 rounded-full flex-shrink-0"
        />
      ) : (
        <div className="h-8 w-8 rounded-full bg-sky-500/20 flex items-center justify-center flex-shrink-0">
          <span className="text-xs font-bold text-sky-400">{tweet.author.name[0]}</span>
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm font-medium text-white truncate">
            @{tweet.author.username}
          </span>
          <span className="text-xs text-gray-500">{formatTimeAgo(tweet.createdAt)}</span>
          <SentimentBadge sentiment={tweet.sentiment} />
        </div>
        <p className="text-xs text-gray-400 line-clamp-2">{tweet.content}</p>
      </div>
    </div>
  </a>
);

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

const TweetMentionCard: React.FC<TweetMentionCardProps> = ({
  tweetUrl,
  tweetId,
  tweetData,
  variant = "card",
}) => {
  // If we have pre-loaded data, use it
  if (tweetData) {
    switch (variant) {
      case "inline":
        return <InlineVariant tweet={tweetData} />;
      case "compact":
        return <CompactVariant tweet={tweetData} />;
      case "card":
      default:
        return <CardVariant tweet={tweetData} />;
    }
  }

  // Fallback: simple link if no data
  const url = tweetUrl || (tweetId ? `https://x.com/i/status/${tweetId}` : null);
  if (!url) return null;

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-gray-800/50 hover:bg-gray-700/50 border border-gray-700/40 text-sky-400 hover:text-sky-300 transition-all text-sm"
    >
      <img src={xLogo} alt="X" className="h-3.5 w-3.5 opacity-70" />
      <span>View Tweet</span>
      <ExternalLink className="h-3 w-3" />
    </a>
  );
};

export default TweetMentionCard;

